import { Flex, Text } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useMemo, useState } from "react";
import type { ColumnDef } from "../../components/base-table-layout";
import { createEhrWriteBackColumn } from "../../ehr/create-ehr-column";
import { ChronicBadge } from "../../../shared-components/fhir-sections/conditions/chronicity";
import { HccBadge } from "../../../shared-components/fhir-sections/conditions/hcc-badge";
import type { FilterToggle } from "../../../shared-components/fhir-sections/fhir-section";
import { useEhrContext } from "../../../shared-logic/ehr-context/ehr-context";
import { BaseTableLayout } from "../../components/base-table-layout";
import { CodingBadge } from "../../components/coding-badge";
import { LabelFilterSegmentedControl } from "../../components/label-filter-segmented-control";
import { TableFilterPopover } from "../../components/table-filter-popover";
import { useTableFilters } from "../../hooks/use-table-filters";
import { createConditionCategoryFilters } from "../../utils/condition-filters";
import { TableRow } from "../../utils/tables-metadata";
import { ConditionRowData } from "./condition-rows";

type ConditionLabelFilter = "all" | "hcc" | "chronic";

const LIGHT_PURPLE = "rgba(180, 150, 195, 0.4)";
const LIGHT_BLUE = "rgba(165, 185, 230, 0.4)";

export type ConditionTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  onPdfView?: (docId: string) => void;
  displayName?: string;
  isRowViewed?: (row: TableRow) => boolean;
};

function extractHccSortValue(codeString: string): number {
  const hccMatch = codeString.match(/HCC:\s*(.+?)(?:$|;)/);
  if (!hccMatch) return 0;

  const hccCodes = hccMatch[1];
  const versionedMatches = Array.from(hccCodes?.matchAll(/(\d+)\s*\(v(\d+)\)/g) || []);
  const simpleMatches = Array.from(hccCodes?.matchAll(/(?:^|[,;]\s*)(\d+)(?!\s*\()/g) || []);

  const allCodes: number[] = [];
  versionedMatches.forEach(match => {
    const code = parseInt(match[1] || "") || 0;
    if (code > 0) allCodes.push(code);
  });
  simpleMatches.forEach(match => {
    const code = parseInt(match[1] || "") || 0;
    if (code > 0) allCodes.push(code);
  });

  return allCodes.length > 0 ? Math.max(...allCodes) : 0;
}

export function ConditionTable({
  rows,
  onRowClick,
  filterQuery,
  displayName = "Conditions",
  isRowViewed,
}: ConditionTableProps) {
  const [labelFilter, setLabelFilter] = useState<ConditionLabelFilter>("all");
  const ehrContext = useEhrContext();

  const columns = useMemo(() => {
    const cols: ColumnDef<ConditionRowData>[] = [
      {
        key: "labels" as keyof ConditionRowData,
        header: "Labels",
        sortable: false,
        maxWidth: "150px",
        render: row => (
          <Flex gap={2}>
            {row.isHcc && <HccBadge />}
            {row.isChronic && <ChronicBadge />}
          </Flex>
        ),
      },
      { key: "condition", header: "Condition", maxWidth: "500px" },
      {
        key: "hasEncounterDiagnosis",
        header: "Has Encounter Diagnosis",
        maxWidth: "150px",
        render: row => <Text>{row.hasEncounterDiagnosis ? "Yes" : "No"}</Text>,
      },
      {
        key: "code",
        header: "Code",
        render: (row, query) => {
          if (!row.codings || row.codings.length === 0) {
            return <Text>-</Text>;
          }

          return (
            <Flex gap={1} wrap="wrap">
              {row.codings.map((coding, index) => (
                <CodingBadge
                  key={`${coding.system}-${coding.code}-${index}`}
                  coding={coding}
                  searchQuery={query}
                />
              ))}
            </Flex>
          );
        },
      },
      { key: "firstSeen", header: "First Seen", maxWidth: "150px" },
      { key: "lastSeen", header: "Last Seen", maxWidth: "150px", sort: "desc" },
    ];

    const ehrColumn = createEhrWriteBackColumn<ConditionRowData>(ehrContext, "condition");
    if (ehrColumn) cols.push(ehrColumn);

    return cols;
  }, [ehrContext]);

  const enhancedRows = useMemo(() => {
    return rows.map(row => ({
      ...row,
      hccSortValue: extractHccSortValue((row as ConditionRowData).code),
    }));
  }, [rows]);

  const categoryFilterOptions = useMemo(() => {
    return createConditionCategoryFilters().map(option => ({
      label: option.label,
      groupLabel: option.groupLabel,
      isChecked: true, // All filters enabled by default
      filterFn: (rowData: ConditionRowData) => {
        const [type, value] = option.value.split(":");
        if (type === "category") {
          return value === "Encounter Diagnosis"
            ? rowData.hasEncounterDiagnosis
            : !rowData.hasEncounterDiagnosis;
        }
        return true;
      },
    }));
  }, []);

  const {
    activeFilters,
    setActiveFilters,
    filteredRows: categoryFilteredRows,
  } = useTableFilters(enhancedRows, categoryFilterOptions);

  const finalFilteredRows = useMemo(() => {
    if (labelFilter === "all") {
      return categoryFilteredRows;
    }
    return categoryFilteredRows.filter(row => {
      const conditionRow = row as ConditionRowData;
      if (labelFilter === "hcc") {
        return conditionRow.isHcc;
      }
      if (labelFilter === "chronic") {
        return conditionRow.isChronic;
      }
      return true;
    });
  }, [categoryFilteredRows, labelFilter]);

  function handleRowClick(row: TableRow) {
    onRowClick(row);
  }

  return (
    <BaseTableLayout
      title={displayName}
      rows={finalFilteredRows as unknown as ConditionRowData[]}
      columns={columns}
      filterQuery={filterQuery}
      onRowClick={handleRowClick}
      isRowViewed={isRowViewed}
      headerActionsSlot={
        <Flex gap={2}>
          <LabelFilterSegmentedControl
            value={labelFilter}
            onValueChange={setLabelFilter}
            items={[
              { value: "all", label: "All" },
              { value: "hcc", label: "HCC", indicatorColor: LIGHT_PURPLE },
              { value: "chronic", label: "Chronic", indicatorColor: LIGHT_BLUE },
            ]}
          />
          <TableFilterPopover
            filters={categoryFilterOptions}
            activeFilters={activeFilters}
            setActiveFilters={(newFilters: FilterToggle[]) => setActiveFilters(newFilters)}
          />
        </Flex>
      }
    />
  );
}
